import React, { useState } from 'react';
import { Button, Modal } from 'antd';

import { observer } from 'mobx-react';
import { EnhancedViewer as Viewer } from '@partsbadger/threedviewer';

/** Matches optional metadata shown in `EnhancedViewer` sidebar (name, L×W×H, volume, shape label). */
export type ViewerPartInfo = {
    name?: string;
    dimensions?: { length: string; width: string; height: string; unit: string };
    shape?: string;
    volume?: string;
};

type QuoteLikePart = {
    name: string;
    product?: {
        dimensions?: string;
        dimensions_dict?: { length: number; width: number; height: number; volume: number; unit: string };
    };
};

/** Map quote line item / cart part fields into viewer metadata. */
export function viewerPartInfoFromQuotePart(part: QuoteLikePart): ViewerPartInfo | undefined {
    const d = part.product?.dimensions_dict;
    const shape = part.product?.dimensions?.trim();
    if (!part.name?.trim() && !d) return undefined;
    return {
        name: part.name,
        ...(shape ? { shape } : {}),
        ...(d
            ? {
                  dimensions: {
                      length: String(d.length),
                      width: String(d.width),
                      height: String(d.height),
                      unit: String(d.unit ?? 'MM'),
                  },
                  volume: String(d.volume),
              }
            : {}),
    };
}

interface Props {
    url: string | null;
    /** Shown in the viewer sidebar when layout has room (name, dimensions, volume). */
    partInfo?: ViewerPartInfo;
    buttonStyles?: object;
    children?: React.ReactNode;
}

export const ThreeDViewerModal = observer((props: Props) => {
    const [modalVisible, setModalVisible] = useState(false);
    /** Bump on every open so EnhancedViewer fully remounts (clean camera / R3F size) even when `url` is unchanged. */
    const [viewerSession, setViewerSession] = useState(0);

    const open3dViewer = async () => {
        setViewerSession(s => s + 1);
        setModalVisible(true);
    };

    const modelUrl = props.url;
    const showViewer = Boolean(modalVisible && modelUrl);

    return (
        <>
            {props.children ? (
                <div onClick={open3dViewer}>{props.children}</div>
            ) : (
                <Button style={props?.buttonStyles} type="default" className="ant-btn-xs" onClick={open3dViewer}>
                    View in 3D
                </Button>
            )}

            <Modal
                open={modalVisible}
                footer={null}
                centered={true}
                width={'min(118.8vw, calc(100vw - 32px))'}
                maskClosable={true}
                onCancel={() => {
                    setModalVisible(false);
                }}
                bodyStyle={{ height: '720px', margin: 0, padding: 0, overflow: 'hidden' }}
                style={{ maxWidth: '1267px' }}
            >
                <div
                    className="w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ position: 'relative', height: '100%', minHeight: 0, boxSizing: 'border-box' }}
                >
                    {showViewer && modelUrl ? (
                        <div style={{ position: 'absolute', inset: 0, minHeight: 0 }}>
                            <Viewer key={`${modelUrl}-${viewerSession}`} url={modelUrl} partInfo={props.partInfo} />
                        </div>
                    ) : null}
                </div>
            </Modal>
        </>
    );
});
